import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, MapPin, Star, Trash2, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import {
  listMyPlans,
  deleteMyPlan,
  getMyVisitedEvents,
  rateEvent,
  clearRating,
  removeVisit,
  updateVisitDate,
} from "@/lib/user-data.functions";
import { SITE_URL } from "@/lib/seo";

type VisitedItem = {
  eventId: string;
  title: string;
  venueName: string;
  category: string;
  city: string;
  visitedOn: string;
  rating: number | null;
};

// US Eastern "today" as YYYY-MM-DD for the date input's max.
function todayET(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Your Account — RanchPlanner" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const listFn = useServerFn(listMyPlans);
  const deleteFn = useServerFn(deleteMyPlan);
  const visitedFn = useServerFn(getMyVisitedEvents);
  const rateFn = useServerFn(rateEvent);
  const clearRatingFn = useServerFn(clearRating);
  const removeVisitFn = useServerFn(removeVisit);
  const updateDateFn = useServerFn(updateVisitDate);
  const [plans, setPlans] = useState<any[]>([]);
  const [items, setItems] = useState<VisitedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, v] = await Promise.all([listFn(), visitedFn()]);
        if (cancelled) return;
        if (p.ok) setPlans(p.plans);
        if (v.ok) setItems(v.items);
      } catch {
        toast.error("Couldn't load your account data");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [listFn, visitedFn]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this plan?")) return;
    const res = await deleteFn({ data: { id } });
    if (res.ok) {
      setPlans((prev) => prev.filter((p) => p.id !== id));
      toast.success("Plan deleted");
    } else {
      toast.error(res.error || "Couldn't delete");
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  const updateItem = (eventId: string, patch: Partial<VisitedItem>) =>
    setItems((prev) => prev.map((it) => (it.eventId === eventId ? { ...it, ...patch } : it)));

  const handleRate = async (eventId: string, rating: number) => {
    const prev = items.find((i) => i.eventId === eventId)?.rating ?? null;
    updateItem(eventId, { rating });
    const res = await rateFn({ data: { eventId, rating } });
    if (!res.ok) {
      updateItem(eventId, { rating: prev });
      toast.error(res.error || "Couldn't save rating");
    }
  };

  const handleDateChange = async (eventId: string, visitedOn: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(visitedOn)) return;
    const prev = items.find((i) => i.eventId === eventId)?.visitedOn ?? visitedOn;
    updateItem(eventId, { visitedOn });
    const res = await updateDateFn({ data: { eventId, visitedOn } });
    if (!res.ok) {
      updateItem(eventId, { visitedOn: prev });
      toast.error(res.error || "Couldn't update date");
    }
  };

  const handleRemoveVisit = async (eventId: string) => {
    if (!confirm("Remove this place from your history?")) return;
    const prev = items;
    setItems((p) => p.filter((it) => it.eventId !== eventId));
    const res = await removeVisitFn({ data: { eventId } });
    if (!res.ok) {
      setItems(prev);
      toast.error(res.error || "Couldn't remove");
      return;
    }
    // Also clear any rating for the event.
    await clearRatingFn({ data: { eventId } }).catch(() => {});
    toast.success("Removed");
  };

  return (
    <div className="min-h-screen pt-6 md:pt-10 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
              Your Account<span className="text-highlight">.</span>
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-foreground/5"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-10">
            <section>
              <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>Saved Plans</h2>
              {plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No saved plans yet. <Link to="/explore" className="text-primary underline">Browse events</Link> to start building one.
                </p>
              ) : (
                <ul className="space-y-2">
                  {plans.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 p-3 rounded-xl border-2 border-border bg-card">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold truncate">{p.name || `Plan from ${new Date(p.created_at).toLocaleDateString()}`}</p>
                        <p className="text-xs text-muted-foreground">{p.event_ids?.length || 0} activities · updated {new Date(p.updated_at).toLocaleDateString()}</p>
                      </div>
                      <a
                        href={`${SITE_URL}/plan?plan=${p.share_token}`}
                        className="p-2 text-muted-foreground hover:text-primary"
                        aria-label="Open plan"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="p-2 text-muted-foreground hover:text-destructive"
                        aria-label="Delete plan"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
                Places You've Been
              </h2>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> Rate places from any event card to start tracking your adventures.
                </p>
              ) : (
                <ul className="space-y-2">
                  {items.map((it) => (
                    <VisitedRow
                      key={it.eventId}
                      item={it}
                      onRate={(n) => handleRate(it.eventId, n)}
                      onDateChange={(d) => handleDateChange(it.eventId, d)}
                      onRemove={() => handleRemoveVisit(it.eventId)}
                    />
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function VisitedRow({
  item,
  onRate,
  onDateChange,
  onRemove,
}: {
  item: VisitedItem;
  onRate: (n: number) => void;
  onDateChange: (d: string) => void;
  onRemove: () => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? item.rating ?? 0;

  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border-2 border-border bg-card">
      <div className="flex-1 min-w-0">
        <p className="font-bold truncate">{item.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {item.city ? `${item.city} · ` : ""}{item.category}
        </p>
      </div>

      <div
        className="inline-flex items-center"
        role="radiogroup"
        aria-label="Your rating"
        onMouseLeave={() => setHover(null)}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= display;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onRate(n)}
              onMouseEnter={() => setHover(n)}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              className="p-0.5 transition-transform duration-150 hover:scale-125 active:scale-95"
            >
              <Star
                className="h-5 w-5"
                fill={filled ? "currentColor" : "none"}
                strokeWidth={filled ? 1.5 : 2}
                style={{
                  color: filled
                    ? "var(--color-highlight)"
                    : "color-mix(in oklab, var(--color-foreground) 35%, transparent)",
                }}
              />
            </button>
          );
        })}
      </div>

      <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <span className="hidden sm:inline">Visited</span>
        <input
          type="date"
          value={item.visitedOn}
          max={todayET()}
          onChange={(e) => onDateChange(e.target.value)}
          className="bg-background border border-border rounded px-2 py-1 text-foreground"
        />
      </label>

      <button
        type="button"
        onClick={onRemove}
        className="p-2 text-muted-foreground hover:text-destructive shrink-0"
        aria-label="Remove from history"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}